import { all, call, fork, put, select, takeLatest } from "typed-redux-saga";
import { Action, configuratorActions } from "./configurator-store";
import { errorActions } from "src/components/error-viewer/error-store";
import { ConfiguratorService, Model_LabelAndReferencePost, ProjectLabelService } from "src/backend/openapi/v1";
import { getContactPerson, getCurrentCustomer } from "src/utils";
import { applicationActions } from "src/components/application-store";
import { RootState } from "src/store";

export function* getConfigurations(action: ReturnType<typeof configuratorActions.getConfigurationsRequest>) {
  try {
    const contactPerson = yield* getContactPerson();
    const customer = yield* getCurrentCustomer();
    if (!contactPerson.identityId) throw Error("Missing identity id");

    const configurationsState = yield* select((state: RootState) => state.pageReducer.combinedConfiguratorReducer.configuratorReducer.configurationsState);
    const appliedFilter = yield* select((state: RootState) => state.pageReducer.combinedConfiguratorReducer.configuratorReducer.appliedFilter);

    // Loading
    if (configurationsState) {
      yield put(applicationActions.setGlobalLoadingBar(true));
    } else {
      yield put(configuratorActions.setIsLoading(true));
    }

    const result = yield* call(ConfiguratorService.postApiV1ConfiguratorConfigurations, action.payload.projectId, contactPerson.identityId, customer.customerId, appliedFilter);
    yield put(configuratorActions.getConfigurationsSuccess(result));
  } catch (error) {
    yield put(errorActions.addError(error));
  } finally {
    yield put(applicationActions.setGlobalLoadingBar(false));
    yield put(configuratorActions.setIsLoading(false));
  }
}

export function* watchGetConfigurations() {
  yield takeLatest(Action.GetConfigurationsRequest, getConfigurations);
}

export function* deleteProjectLineItems(action: ReturnType<typeof configuratorActions.deleteProjectLineItemsRequest>) {
  try {
    const contactPerson = yield* getContactPerson();
    const customer = yield* getCurrentCustomer();
    if (!contactPerson.identityId) throw Error("Missing identity id");

    yield put(applicationActions.setGlobalLoading(true));
    yield* call(ConfiguratorService.postApiV1ConfiguratorProjectLineItemsDelete, contactPerson.identityId, customer.customerId, undefined, action.payload.data);
    action.payload.callBack(true);
  } catch (error) {
    yield put(errorActions.addError(error));
    action.payload.callBack(false);
  } finally {
    yield put(applicationActions.setGlobalLoading(false));
  }
}

export function* watchdeleteProjectLineItems() {
  yield takeLatest(Action.DeleteProjectLineItemsRequest, deleteProjectLineItems);
}

export function* copyProjectLineItems(action: ReturnType<typeof configuratorActions.copyProjectLineItemsRequest>) {
  try {
    const contactPerson = yield* getContactPerson();
    const customer = yield* getCurrentCustomer();
    if (!contactPerson.identityId) throw Error("Missing identity id");

    yield put(applicationActions.setGlobalLoading(true));
    yield* call(ConfiguratorService.postApiV1ConfiguratorProjectLineItemsCopy, contactPerson.identityId, customer.customerId, undefined, action.payload.blankValues, action.payload.data);
    action.payload.callBack(true);
  } catch (error) {
    yield put(errorActions.addError(error));
    action.payload.callBack(false);
  } finally {
    yield put(applicationActions.setGlobalLoading(false));
  }
}

export function* watchcopyProjectLineItems() {
  yield takeLatest(Action.CopyProjectLineItemsRequest, copyProjectLineItems);
}

export function* getLabels(action: ReturnType<typeof configuratorActions.getLabelsRequest>) {
  try {
    const result = yield* call(ProjectLabelService.getApiV1LabelProject, action.payload.projectId);
    yield put(configuratorActions.getLabelsSuccess(result));
  } catch (error) {
    yield put(errorActions.addError(error));
  }
}

export function* watchgetLabels() {
  yield takeLatest(Action.GetLabelsRequest, getLabels);
}

export function* postLabels(action: ReturnType<typeof configuratorActions.postLabelsRequest>) {
  try {
    const labels = yield* select((state: RootState) => state.pageReducer.combinedConfiguratorReducer.configuratorReducer.labels || []);

    const selectedLabels = action.payload.labels.map(selectedLabel => {
      const foundLabel = labels.find(label => label.shortname === selectedLabel);
      return {
        labelId: foundLabel?.labelId || undefined,
        shortName: selectedLabel
      };
    });

    const payload: Model_LabelAndReferencePost = {
      entityIds: action.payload.entityIds,
      labels: selectedLabels
    };

    const result = yield* call(ProjectLabelService.postApiV1LabelProject, action.payload.projectId, payload);
    yield put(configuratorActions.getLabelsSuccess(result));
    action.payload.callback(true, result);
  } catch (error) {
    yield put(errorActions.addError(error));
    action.payload.callback(false);
  }
}

export function* watchpostLabels() {
  yield takeLatest(Action.PostLabelsRequest, postLabels);
}

export function* deleteConfiguration(action: ReturnType<typeof configuratorActions.deleteConfigurationsRequest>) {
  try {
    const contactPerson = yield* getContactPerson();
    const customer = yield* getCurrentCustomer();
    if (!contactPerson.identityId) throw Error("Missing identity id");

    yield put(applicationActions.setGlobalLoading(true));
    yield* call(ConfiguratorService.postApiV1ConfiguratorConfigurationDelete, contactPerson.identityId, customer.customerId, action.payload.data);
    action.payload.callBack(true);
  } catch (error) {
    yield put(errorActions.addError(error));
    action.payload.callBack(false);
  } finally {
    yield put(applicationActions.setGlobalLoading(false));
  }
}

export function* watchdeleteConfiguration() {
  yield takeLatest(Action.DeleteConfigurationRequest, deleteConfiguration);
}

export function* configuratorSaga() {
  yield all([fork(watchGetConfigurations), fork(watchdeleteProjectLineItems), fork(watchcopyProjectLineItems), fork(watchgetLabels), fork(watchpostLabels), fork(watchdeleteConfiguration)]);
}
